import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Mountain, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';

// Registrazione dedicata "Guida del Parco": crea uno stakeholder con tenant_type=guida (pending).
export default function GuidaRegister() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', company_name: '', phone: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await register({ ...form, tenant_type: 'guida' });
      if (data?.status === 'verify_email') {
        navigate(`/verifica-email?email=${encodeURIComponent(form.email)}`);
        return;
      }
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <CheckCircle2 className="h-7 w-7 text-primary" />
          </div>
          <h1 className="font-display text-xl font-bold">Richiesta inviata!</h1>
          <p className="mt-2 text-sm text-muted-foreground">La tua candidatura come Guida del Parco è in attesa di approvazione dell'amministratore. Riceverai accesso alla tua area guida non appena verrà approvata.</p>
          <Button className="mt-6 w-full" onClick={() => navigate('/guida/accedi')} data-testid="guida-goto-login">Vai al login</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-10">
      <div className="w-full max-w-lg">
        <Link to="/" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="guida-back"><ArrowLeft className="h-4 w-4" />Torna alla Home</Link>
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Mountain className="h-5 w-5" /></div>
          <div>
            <p className="font-display text-lg font-bold">Sila Experiences</p>
            <p className="text-xs text-muted-foreground">Registrazione Guida del Parco</p>
          </div>
        </div>
        <Card className="p-6 sm:p-8">
          <h1 className="font-display text-2xl font-bold tracking-tight">Diventa Guida del Parco</h1>
          <p className="mt-1 text-sm text-muted-foreground">Compila il modulo: dopo l'approvazione dell'amministratore potrai creare itinerari e riceverli sulla mappa 3D del Parco.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="guida-register-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label htmlFor="g-name">Nome e cognome</Label><Input id="g-name" required value={form.name} onChange={upd('name')} data-testid="guida-name-input" /></div>
              <div className="space-y-2"><Label htmlFor="g-company">Nome guida / attività</Label><Input id="g-company" required value={form.company_name} onChange={upd('company_name')} placeholder="es. Guide Sila Trek" data-testid="guida-company-input" /></div>
            </div>
            <div className="space-y-2"><Label htmlFor="g-email">Email</Label><Input id="g-email" type="email" autoComplete="username" required value={form.email} onChange={upd('email')} data-testid="guida-email-input" /></div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label htmlFor="g-password">Password</Label><Input id="g-password" type="password" autoComplete="new-password" required minLength={8} value={form.password} onChange={upd('password')} placeholder="min 8 caratteri" data-testid="guida-password-input" /></div>
              <div className="space-y-2"><Label htmlFor="g-phone">Telefono</Label><Input id="g-phone" value={form.phone} onChange={upd('phone')} placeholder="+39..." data-testid="guida-phone-input" /></div>
            </div>
            {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="guida-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="guida-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Invia candidatura
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Hai già un account? <Link to="/guida/accedi" className="font-medium text-primary hover:underline">Accedi</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
